/**
 * Dialog for adding USDC liquidity to a market
 */
import { formatUsdc } from "@breakout/sdk"
import { toast } from "@breakout/ui/components/Toast"
import { AlertCircle, Check, DollarSign, Loader2, X } from "lucide-react"
import { useCallback, useState } from "react"
import type { Address } from "viem"
import { useAddLiquidity } from "../hooks/useAddLiquidity"

interface AddLiquidityDialogProps {
	marketAddress: Address
	marketName: string
	onClose: () => void
	virtualLiquidityUsdc?: number
}

type DialogStep = "input" | "approving" | "adding" | "success"

export function AddLiquidityDialog({
	marketAddress,
	marketName,
	onClose,
	virtualLiquidityUsdc = 0,
}: AddLiquidityDialogProps) {
	const [amount, setAmount] = useState("")
	const [step, setStep] = useState<DialogStep>("input")
	const [error, setError] = useState<string | null>(null)

	const {
		marketBalance,
		isLoadingMarketBalance,
		userBalance,
		isLoadingUserBalance,
		needsApproval,
		hasSufficientBalance,
		approve,
		isApproving,
		addLiquidity,
		isAddingLiquidity,
	} = useAddLiquidity({ marketAddress })

	const parsedAmount = Number(amount) || 0

	const handleSubmit = useCallback(async () => {
		if (parsedAmount <= 0) {
			setError("Please enter a valid amount")
			return
		}

		if (!hasSufficientBalance(parsedAmount)) {
			setError("Insufficient USDC balance")
			return
		}

		setError(null)

		try {
			// Check if approval is needed
			if (needsApproval(parsedAmount)) {
				setStep("approving")
				await approve({ amount: parsedAmount })
				toast.success("USDC approved")
			}

			// Add liquidity
			setStep("adding")
			await addLiquidity({ amount: parsedAmount })
			setStep("success")
			toast.success(`Added ${formatUsdc(parsedAmount)} liquidity`)
		} catch (err) {
			setError(err instanceof Error ? err.message : "Transaction failed")
			setStep("input")
		}
	}, [parsedAmount, hasSufficientBalance, needsApproval, approve, addLiquidity])

	const isProcessing = isApproving || isAddingLiquidity

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
			<div className="w-full max-w-md rounded-xl border border-gray-700 bg-gray-900 p-6 shadow-2xl">
				{/* Header */}
				<div className="mb-6 flex items-center justify-between">
					<h2 className="font-semibold text-lg text-white">Add Liquidity</h2>
					<button
						aria-label="Close dialog"
						className="rounded-lg p-1 text-gray-400 hover:bg-gray-800 hover:text-white"
						disabled={isProcessing}
						onClick={onClose}
						type="button"
					>
						<X className="size-5" />
					</button>
				</div>

				{step === "success" ? (
					<div className="space-y-6">
						<div className="flex flex-col items-center gap-3 py-4">
							<div className="flex size-12 items-center justify-center rounded-full bg-green-500/20">
								<Check className="size-6 text-green-400" />
							</div>
							<p className="text-center text-gray-300">
								Successfully added {formatUsdc(parsedAmount)} to {marketName}
							</p>
						</div>
						<button
							className="w-full rounded-lg bg-gray-700 py-3 font-medium text-white hover:bg-gray-600"
							onClick={onClose}
							type="button"
						>
							Done
						</button>
					</div>
				) : (
					<div className="space-y-5">
						{/* Market Info */}
						<div className="rounded-lg border border-gray-700 bg-gray-800/50 p-4">
							<p className="mb-1 text-gray-400 text-sm">Market</p>
							<p className="font-medium text-white">{marketName}</p>
							<p className="mt-3 text-gray-400 text-sm">Current Liquidity</p>
							<p className="font-medium text-white">
								{isLoadingMarketBalance ? (
									<span className="inline-block h-5 w-20 animate-pulse rounded bg-gray-700" />
								) : (
									`${formatUsdc((marketBalance?.usdc ?? 0) + virtualLiquidityUsdc)}`
								)}
							</p>
						</div>

						{/* Amount Input */}
						<div>
							<label className="mb-2 block text-gray-400 text-sm" htmlFor="amount">
								Amount (USDC)
							</label>
							<div className="relative">
								<span className="absolute top-1/2 left-3 -translate-y-1/2 text-gray-500">$</span>
								<input
									className="w-full rounded-lg border border-gray-700 bg-gray-800 py-3 pr-4 pl-8 text-white placeholder-gray-500 focus:border-gray-600 focus:outline-none focus:ring-1 focus:ring-gray-600"
									disabled={isProcessing}
									id="amount"
									inputMode="decimal"
									onChange={e => {
										setAmount(e.target.value)
										setError(null)
									}}
									placeholder="0.00"
									type="text"
									value={amount}
								/>
							</div>
							<p className="mt-2 text-gray-500 text-xs">
								Your balance:{" "}
								{isLoadingUserBalance ? (
									<span className="inline-block h-3 w-16 animate-pulse rounded bg-gray-700" />
								) : (
									`${formatUsdc(userBalance?.formatted ?? 0)}`
								)}
							</p>
						</div>

						{/* Error Message */}
						{error && (
							<div className="flex items-center gap-2 rounded-lg bg-red-500/10 px-3 py-2 text-red-400 text-sm">
								<AlertCircle className="size-4 shrink-0" />
								<span>{error}</span>
							</div>
						)}

						{/* Insufficient Balance Warning */}
						{parsedAmount > 0 &&
							!isLoadingUserBalance &&
							!hasSufficientBalance(parsedAmount) &&
							!error && (
								<div className="flex items-center gap-2 rounded-lg bg-yellow-500/10 px-3 py-2 text-sm text-yellow-400">
									<AlertCircle className="size-4 shrink-0" />
									<span>
										Insufficient balance. Need{" "}
										{formatUsdc(parsedAmount - (userBalance?.formatted ?? 0))} more.
									</span>
								</div>
							)}

						{/* Submit Button */}
						<button
							className="flex w-full items-center justify-center gap-2 rounded-lg bg-white py-3 font-medium text-gray-900 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
							disabled={
								isProcessing ||
								parsedAmount <= 0 ||
								(userBalance !== null && !hasSufficientBalance(parsedAmount))
							}
							onClick={handleSubmit}
							type="button"
						>
							{isProcessing ? (
								<>
									<Loader2 className="size-4 animate-spin" />
									{step === "approving" ? "Approving USDC..." : "Adding Liquidity..."}
								</>
							) : needsApproval(parsedAmount) && parsedAmount > 0 ? (
								<>
									<DollarSign className="size-4" />
									Approve & Add Liquidity
								</>
							) : (
								<>
									<DollarSign className="size-4" />
									Add Liquidity
								</>
							)}
						</button>

						{/* Approval Note */}
						{needsApproval(parsedAmount) && parsedAmount > 0 && !isProcessing && (
							<p className="text-center text-gray-500 text-xs">
								This will require two transactions: approval and add liquidity
							</p>
						)}
					</div>
				)}
			</div>
		</div>
	)
}
